/**
 * ArkUI.Keyboard —— 软键盘是盖在界面上的一层：WebView 尺寸恒定，避让由本模块按区域完成。
 *
 * 背景（2026-10-17 起）：Android 宿主（edge-to-edge）原先把 IME 高度加成内容根视图的底 padding，WebView 真的变矮
 * —— 等于老式全窗口 adjustResize：整页跟着变矮，分栏页里与输入无关的那一栏（点单页购物车）被连带压扁、底栏被抬到
 * 键盘上方；浮动 Dialog 跟着容器缩 max-h 并重新居中。现在原生只报数（MainActivity → ImeInsetSignal → MainPage
 * EvaluateJavascript → set()），布局一像素不变，键盘出现 / 高度变化 / 键盘开着时换焦点、打字、DOM 变化时按顺序：
 *
 *   ① 焦点在可抬升面板里：浮动居中 Dialog（data-ark-kb-float）尺寸不变，在键盘上方的区域里重新居中（同 Android 对话框
 *      避让键盘的位置，底栏一起露出）；放不下就贴到上边距，再在面板内部把焦点框滚上来。
 *      手机贴底 Sheet（data-ark-kb-sheet，窄屏）整体坐到键盘上，太高就收矮、内部滚动。
 *   ② 焦点所在的纵向滚动区（由内往外，露出焦点框即止）：被键盘盖住的部分补白，再滚到框底离键盘 GAP。
 *   ③ 键盘区域内的其他纵向滚动区：只补白、不滚动 —— 内容位置不变，最后几项也滚得到键盘上方
 *      （「上面输入框、下面结果列表」靠这条成立，组件不用声明）。焦点框的祖先滚动区不在此列，归 ②。
 *   ④ 以上都不成立、焦点框仍被盖住：整页上推刚好露出（= adjustPan），并在控制台告警 —— 该界面应补一个滚动区。
 *
 * 补白 = 滚动区的底部内边距加上被盖住的高度（同原生给列表设 paddingBottom = 键盘遮挡高度），不是在内容末尾追加空白：
 * POS 里很多滚动区的内容是撑满的（空态 h-full、ArkScrollbar 内容层 min-height:100%），追加的空白会原样变成凭空的
 * 滚动距离；内边距让撑满的子元素按缩小后的内容盒算高，内容真伸到键盘后面才可滚。
 *
 * 键盘收起：撤补白、面板回位、撤上推。键盘打开期间补白与上移量只增不减，免得在字段间切换时上下弹跳（iPadOS 同）。
 *
 * 上移动 `top` 而不是 transform / translate：Dialog 内的下拉（ark-floating）是原位渲染的 position:fixed，
 * 祖先一旦带 transform/translate 就会改成相对面板定位而错位。这是动效规范「只动 transform/opacity」的有意例外。
 * ④ 的整页上推用 body transform：此时所有 fixed 层本就要跟着一起走。
 *
 * 宿主：Android POS 由原生调 set()（首次调用即启用）；其它宿主不调用则零副作用。
 * simulate(高度) 在页面底部画一块假键盘并走同一套逻辑，供 Windows / 无头浏览器走查。
 * 规范：docs/design/客户端界面/设计方案.md「软键盘」。
 */
window.ArkUI = window.ArkUI || {};

(function () {
    // 焦点框底边与键盘顶之间留的距离；面板上移 / 整页上推后与屏幕顶的最小距离
    const GAP = 16;
    const TOP_MARGIN = 16;
    // ③ 的候选滚动区：按类名粗筛再用 computed style 确认（全 DOM 逐个 getComputedStyle 太贵）。
    // ② 不靠它 —— 焦点框的滚动祖先逐层用 computed style 判定，写在 CSS 文件里的滚动区也找得到。
    // .pos-native-scroll / .kds-scroll 是 POS 在 app.css 里定义的原生滚动类，类名里不带 overflow-*（历史订单的订单表等）
    const SCROLLER_SELECTOR = '[class*="overflow-y-auto"],[class*="overflow-auto"],[class*="overflow-y-scroll"],[class*="overflow-scroll"],.ark-scrollbar-viewport,.pos-native-scroll,.kds-scroll';

    let enabled = false;
    /** 键盘高度（CSS px），0 = 收起 */
    let kbH = 0;
    /** 当前抬升中的面板：{ panel, kind: 'float' | 'sheet', shift } */
    let lift = null;
    let dropTimer = 0;
    let notifyTimer = 0;
    /** 已补白的滚动区 → { px 补白量, base 原底部内边距, value 写入的内联值, prev / prevPriority 原内联值 } */
    const padded = new Map();
    /** 整页上推量（px） */
    let pan = 0;
    let observer = null;
    let mutateTimer = 0;
    let avoidTimer = 0;
    let simEl = null;

    const root = () => document.documentElement;
    const kbTop = () => window.innerHeight - kbH;

    function isEditable(el) {
        if (!el || el.disabled || el.readOnly) return false;
        if (el.isContentEditable) return true;
        if (el.tagName === 'TEXTAREA') return true;
        if (el.tagName !== 'INPUT') return false;
        return !/^(button|checkbox|radio|range|color|file|submit|reset|image|hidden)$/i.test(el.type);
    }

    function focusedField() {
        const el = document.activeElement;
        return isEditable(el) ? el : null;
    }

    function isVScroller(el) {
        // ArkScrollbar 视口两向都是 overflow: scroll，只横向的实例（ShowVertical=false）靠有没有纵向轨道区分
        if (el.classList.contains('ark-scrollbar-viewport')) {
            return !!el.parentElement && !!el.parentElement.querySelector(':scope > .ark-scrollbar-track-y');
        }
        const oy = getComputedStyle(el).overflowY;
        return oy === 'auto' || oy === 'scroll';
    }

    function durationMs() {
        const v = getComputedStyle(root()).getPropertyValue('--ark-dur-base').trim();
        const n = parseFloat(v);
        return isNaN(n) ? 150 : (v.endsWith('ms') ? n : n * 1000);
    }

    // ── 补白：滚动区的内联 padding-bottom = 原值 + 被盖住的高度（原因见文件头）。按元素各自写，取值只增不减。
    //    内联 !important：要压过 Tailwind 的 p-* / pb-*，包括层内的 important ──

    function setPad(el, px) {
        let rec = padded.get(el);
        // 内联样式被整体改写过（Blazor 重渲染了 style 属性）：按现在的样式重取原值再补
        if (rec && el.style.getPropertyValue('padding-bottom') !== rec.value) {
            padded.delete(el);
            px = Math.max(px, rec.px);
            rec = null;
        }
        if (px <= 0 || (rec && rec.px >= px)) return;
        if (!rec) {
            rec = {
                base: parseFloat(getComputedStyle(el).paddingBottom) || 0,
                prev: el.style.getPropertyValue('padding-bottom'),
                prevPriority: el.style.getPropertyPriority('padding-bottom')
            };
            padded.set(el, rec);
        }
        rec.px = px;
        rec.value = (rec.base + px) + 'px';
        el.style.setProperty('padding-bottom', rec.value, 'important');
    }

    function clearPads() {
        padded.forEach((rec, el) => {
            // 期间被别人改写过就不动它
            if (el.style.getPropertyValue('padding-bottom') !== rec.value) return;
            if (rec.prev) el.style.setProperty('padding-bottom', rec.prev, rec.prevPriority);
            else el.style.removeProperty('padding-bottom');
        });
        padded.clear();
    }

    // ── ① 可抬升面板 ──

    const LIFT_SELECTOR = '[data-ark-kb-float],[data-ark-kb-sheet]';

    /** 面板在当前宽度下的抬升方式。窄屏下带 data-ark-kb-mobile 的浮动面板是手机呈现：Sheet 走坐到键盘上，FullScreen 走 ②。 */
    function liftKind(panel) {
        const narrow = window.innerWidth < 768;
        if (narrow && panel.hasAttribute('data-ark-kb-sheet')) return 'sheet';
        if (panel.hasAttribute('data-ark-kb-float') && !(narrow && panel.hasAttribute('data-ark-kb-mobile'))) return 'float';
        return null;
    }

    /** 浮动面板此刻实际的上移量（读 computed top，过渡进行中也准）。量到的坐标加上它 = 未上移基准 */
    function shiftNow(panel) {
        return -(parseFloat(getComputedStyle(panel).top) || 0);
    }

    /** 焦点所在的可抬升面板 */
    function liftTarget(field) {
        const panel = field.closest(LIFT_SELECTOR);
        const kind = panel ? liftKind(panel) : null;
        return kind ? { panel, kind } : null;
    }

    function engageLift(target) {
        if (lift && lift.panel === target.panel && lift.kind === target.kind) {
            cancelDrop();
            return;
        }
        if (lift) dropLiftNow();
        if (target.kind === 'float') {
            // 锁住键盘前的高度：内部滚动区补白后内容变多，按内容定高的面板会跟着长高、重新居中，顶边被推出屏幕。
            // 必须在打标记之前量：标记一上，锁定规则的 max-height: none 就生效，内容超长的面板会量出整段内容高
            root().style.setProperty('--ark-kb-lock-h', target.panel.offsetHeight + 'px');
        }
        lift = { panel: target.panel, kind: target.kind, shift: 0 };
        lift.panel.setAttribute('data-ark-kb-lift', target.kind);
        if (target.kind === 'sheet') {
            // 坐到键盘上之后可用高度变小：收矮到键盘以上（留 TOP_MARGIN），内容区内部滚动。
            // 必须写内联 !important：手机 Sheet 的 max-md:max-h-[85vh]! 是层内 important，样式表里不分层的
            // important 压不过它（层叠规则对 important 反转）；内联 important 才压得过。
            lift.panel.style.setProperty('max-height', `calc(100% - var(--ark-kb-h, 0px) - ${TOP_MARGIN}px)`, 'important');
        } else {
            root().style.setProperty('--ark-kb-shift', '0px');
        }
    }

    /**
     * 浮动面板：尺寸不变，在键盘上方的区域（上边距 ~ 键盘顶留 GAP）里居中 —— Android 对话框避让键盘落在同一位置，
     * 底栏跟着露出。放不下就贴到上边距，再在面板内部把焦点框滚上来。
     * 坐标先还原成「未上移」基准再算（shiftNow）。
     */
    function layoutFloat(field) {
        const panel = lift.panel;
        const visibleBottom = kbTop();
        const p = panel.getBoundingClientRect();
        const top0 = p.top + shiftNow(panel);
        const maxShift = Math.max(0, top0 - TOP_MARGIN);
        const centered = top0 - (TOP_MARGIN + (visibleBottom - GAP - TOP_MARGIN - p.height) / 2);
        const shift = Math.round(Math.min(Math.max(centered, lift.shift, 0), maxShift));
        lift.shift = shift;
        root().style.setProperty('--ark-kb-shift', shift + 'px');

        let sp = null;
        if (field && panel.contains(field)) {
            for (let n = field.parentElement; n && n !== panel; n = n.parentElement) {
                if (isVScroller(n)) { sp = n; break; }
            }
        }
        if (sp) {
            // 补白量按滚动区自己被盖住的那截算：面板底下被盖的可能是滚动区之外的底栏，补进滚动区只会凭空多出滚动
            setPad(sp, Math.round(sp.getBoundingClientRect().bottom + shiftNow(panel) - shift - visibleBottom));
            // 贴顶了仍盖着：面板内部把字段滚上来。补白后再量 —— 撑满型内容按缩小的内容盒重排，字段可能已经上移
            const need = field.getBoundingClientRect().bottom + shiftNow(panel) - shift + GAP - visibleBottom;
            if (need > 0) sp.scrollTop += Math.ceil(need);
        }

        // 面板位置变了但没有 scroll/resize —— ark-floating 的 autoUpdate 收不到，过渡结束后补一次
        notifyLater(durationMs() + 20);
    }

    function cancelDrop() {
        if (dropTimer) {
            clearTimeout(dropTimer);
            dropTimer = 0;
        }
    }

    function dropLiftNow() {
        cancelDrop();
        if (!lift) return;
        lift.panel.removeAttribute('data-ark-kb-lift');
        lift.panel.style.removeProperty('max-height');
        root().style.removeProperty('--ark-kb-shift');
        root().style.removeProperty('--ark-kb-lock-h');
        lift = null;
    }

    /** 浮动面板先把上移归零让 top 过渡回去（top 从长度到 auto 不会过渡），结束后再摘标记；Sheet 直接回位。 */
    function dropLift() {
        if (!lift) return;
        if (lift.kind !== 'float' || !lift.panel.isConnected) {
            dropLiftNow();
            return;
        }
        lift.shift = 0;
        root().style.setProperty('--ark-kb-shift', '0px');
        cancelDrop();
        dropTimer = setTimeout(dropLiftNow, durationMs() + 20);
        notifyLater(0);
    }

    // ── ② 焦点所在的滚动区（由内往外，露出焦点框即止）──

    function revealInScrollers(field) {
        const top = kbTop();
        const stop = lift && lift.kind === 'float' ? lift.panel : null;
        for (let n = field.parentElement; n && n !== document.body && n !== root(); n = n.parentElement) {
            if (n === stop) break;
            if (!isVScroller(n)) continue;
            const r = n.getBoundingClientRect();
            const coveredByKb = r.bottom > top;
            // 这一层几乎整个在键盘后面：补白滚动也露不出来，交给外层 / 兜底
            if (Math.min(r.bottom, top) - r.top < 24) continue;
            if (coveredByKb) setPad(n, Math.round(r.bottom - top));

            const f = field.getBoundingClientRect();
            const target = coveredByKb ? top - GAP : r.bottom;
            // 不把框顶推出这一层的可见区（多行输入框比可见区还高时，宁可露出顶部）
            const delta = Math.min(f.bottom - target, f.top - r.top - 8);
            if (delta > 0) n.scrollTop += Math.ceil(delta);
            // 这一层已经露出焦点框就停：再往外多半是「内容撑满」的布局壳（全屏 Dialog 内容区等），补了只会让整块连同底栏可拖
            if (field.getBoundingClientRect().bottom <= top) break;
        }
    }

    // ── ③ 键盘区域内的其他滚动区：只补白 ──

    function padScrollers() {
        const top = kbTop();
        const field = focusedField();
        const nodes = document.querySelectorAll(SCROLLER_SELECTOR);
        for (let i = 0; i < nodes.length; i++) {
            const el = nodes[i];
            // 焦点框的祖先归 ②：② 只补到露出焦点框的那一层，外层在这里补就前功尽弃
            if (field && el.contains(field)) continue;
            // 可抬升面板内部只在焦点进去时由 ① 处理（浮动面板按「上移后」的几何补白，Sheet 坐到键盘上后不再被盖）。
            // 这里先补会让按内容定高的面板长高 —— 键盘开着时新弹出的面板就会这样
            const pnl = el.closest(LIFT_SELECTOR);
            if (pnl && liftKind(pnl)) continue;
            const r = el.getBoundingClientRect();
            if (r.height <= 0 || r.top >= top || r.bottom <= top) continue;
            if (!isVScroller(el)) continue;
            setPad(el, Math.round(r.bottom - top));
        }
    }

    // ── ④ 兜底：整页上推 ──

    function setPan(px) {
        pan = px;
        if (px > 0) {
            root().style.setProperty('--ark-kb-pan', px + 'px');
            root().setAttribute('data-ark-kb-pan', '');
        } else {
            root().removeAttribute('data-ark-kb-pan');
            root().style.removeProperty('--ark-kb-pan');
        }
    }

    function fallbackPan(field) {
        const f = field.getBoundingClientRect();
        // 量到的坐标已含当前上推量，先还原
        const bottom0 = f.bottom + pan;
        const top0 = f.top + pan;
        const need = Math.ceil(bottom0 + GAP - kbTop());
        if (need <= pan) return;
        const next = Math.min(need, Math.max(0, Math.floor(top0 - TOP_MARGIN)));
        if (next <= pan) return;
        console.warn('[ArkUI.Keyboard] 焦点框不在可滚动区域内、被键盘盖住，已整页上推 ' + next + 'px。该界面应给输入区补一个滚动容器：', field);
        setPan(next);
    }

    // ── 编排 ──

    function avoid() {
        if (!kbH) return;
        const field = focusedField();
        const target = field ? liftTarget(field) : null;
        if (target) engageLift(target);
        else if (lift) dropLift();

        if (target && target.kind === 'float') layoutFloat(field);
        padScrollers();
        if (field && !(lift && lift.kind === 'float' && lift.panel.contains(field))) {
            revealInScrollers(field);
            fallbackPan(field);
        }
    }

    /**
     * DOM 变化（搜索结果重渲染、新弹窗出现）：本批变更结束后补一次 ③；抬升的面板没了就回位。
     * 用宏任务而不是 rAF：Blazor 一个渲染批次在同一个任务里同步落完，下一个任务里补白照样赶在绘制前；
     * 无头浏览器（走查 / 自动化）的虚拟时间里 rAF 根本不触发。
     */
    function onMutate() {
        if (mutateTimer) return;
        mutateTimer = setTimeout(() => {
            mutateTimer = 0;
            if (!kbH) return;
            if (lift && !lift.panel.isConnected) dropLiftNow();
            padScrollers();
        }, 0);
    }

    /** 换焦点 / 打字：等这一轮事件处理完再算一次（打字时多行输入框会长高，底部几行可能长到键盘后面） */
    function scheduleAvoid() {
        if (!kbH || avoidTimer) return;
        avoidTimer = setTimeout(() => {
            avoidTimer = 0;
            avoid();
        }, 0);
    }

    /** 让 ark-floating 的 autoUpdate 重算（它的避让边界读 height()） */
    function notifyLater(ms) {
        clearTimeout(notifyTimer);
        notifyTimer = setTimeout(() => window.dispatchEvent(new Event('scroll')), ms);
    }

    function apply(h) {
        h = h > 1 ? Math.round(h) : 0;
        if (h === kbH) return;
        const wasOpen = kbH > 0;
        kbH = h;

        if (h > 0) {
            root().style.setProperty('--ark-kb-h', h + 'px');
            root().setAttribute('data-ark-kb', '');
            if (!wasOpen) {
                observer = observer || new MutationObserver(onMutate);
                observer.observe(document.body, { childList: true, subtree: true });
            }
            avoid();
        } else {
            if (observer) observer.disconnect();
            if (mutateTimer) {
                clearTimeout(mutateTimer);
                mutateTimer = 0;
            }
            if (avoidTimer) {
                clearTimeout(avoidTimer);
                avoidTimer = 0;
            }
            root().removeAttribute('data-ark-kb');
            root().style.removeProperty('--ark-kb-h');
            clearPads();
            setPan(0);
            dropLift();
        }
        notifyLater(0);
    }

    function enable() {
        if (enabled) return;
        enabled = true;
        document.addEventListener('focusin', scheduleAvoid, true);
        document.addEventListener('input', scheduleAvoid, true);
    }

    window.ArkUI.Keyboard = {
        /** 启用（重复调用无副作用）。set / simulate 首次调用时自动启用。 */
        enable,

        /**
         * 原生上报（Android MainActivity → ImeInsetSignal → MainPage）。
         * @param {number} imePx 键盘高度（物理像素，从窗口底边量）
         * @param {number} viewPx WebView 高度（物理像素）
         * @param {boolean} visible 键盘是否可见
         * 换算用 innerHeight / viewPx 的比值：界面缩放走 viewport meta，失效时是静默的；比值不依赖它是否生效。
         */
        set(imePx, viewPx, visible) {
            enable();
            let h = 0;
            if (visible && imePx > 0) {
                h = viewPx > 0 ? imePx * window.innerHeight / viewPx : imePx / (window.devicePixelRatio || 1);
            }
            apply(h);
        },

        /** 走查用：在页面底部画一块 h 像素（CSS px）高的假键盘，走同一套避让；0 = 收起。 */
        simulate(h) {
            enable();
            h = Math.max(0, h || 0);
            if (h > 0) {
                if (!simEl) {
                    simEl = document.createElement('div');
                    simEl.setAttribute('data-ark-kb-sim', '');
                    // 挂在 <html> 而不是 <body>：不受 ④ 的 body 上推影响，也不触发 body 上的 DOM 监听
                    Object.assign(simEl.style, {
                        position: 'fixed', left: '0', right: '0', bottom: '0', zIndex: '2147483647',
                        pointerEvents: 'none', background: 'rgba(96, 96, 96, 0.55)'
                    });
                    root().appendChild(simEl);
                }
                simEl.style.height = h + 'px';
            } else if (simEl) {
                simEl.remove();
                simEl = null;
            }
            apply(h);
        },

        /** 键盘是否打开 */
        isOpen() {
            return kbH > 0;
        },

        /** 键盘高度（CSS px），收起时 0 */
        height() {
            return kbH;
        }
    };
})();
